import { Fragment, type ReactElement, useMemo } from 'react';
import { Selector } from '@repo/design-system-kit/components/selector/selector';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import { FAQLink } from '@repo/shared-kit/components/faq-link/faq-link';
import styles from './business-activity.module.css';

interface BusinessActivityProps {
  className?: string;
  isAssociation?: boolean;
  loadingActivity?: string | null;
  onSelect: (activity: BusinessActivityType) => void;
  shouldHideFinancialInstitution?: boolean;
}

interface BusinessActivityConfig {
  key: string;
  showDividerBefore?: boolean;
  value: string;
}

type BusinessActivityType = (typeof BUSINESS_ACTIVITIES)[number]['key'];

const BUSINESS_ACTIVITIES: BusinessActivityConfig[] = [
  {
    key: 'anfe' as const,
    value: 'active_nfe',
  },
  {
    key: 'pnfe' as const,
    value: 'passive_nfe',
  },
  {
    key: 'financial-institution' as const,
    showDividerBefore: true,
    value: 'financial_institution',
  },
];

// TODO: migrate to accept strings for title and subtitle instead of deriving them from isAssociation
export function BusinessActivity({
  className,
  isAssociation,
  loadingActivity,
  onSelect,
  shouldHideFinancialInstitution,
}: BusinessActivityProps): ReactElement {
  const { formatMessage } = useIntl();

  const filteredActivities = useMemo(() => {
    if (shouldHideFinancialInstitution) {
      return BUSINESS_ACTIVITIES.filter(activity => activity.key !== 'financial-institution');
    }
    return BUSINESS_ACTIVITIES;
  }, [shouldHideFinancialInstitution]);

  let title = formatMessage({
    id: 'customer-registration-kit.business-activity-v2.title',
  });
  if (isAssociation) {
    title = formatMessage({
      id: 'customer-registration-kit.business-activity.associations.title',
    });
  }

  return (
    <div className={cx(styles.wrapper, className ?? '')} data-testid="business-activity">
      <div className={styles.sectionWrapper}>
        <h1 className="heading-section">{title}</h1>
        <p className="body-standard">
          <FormattedMessage
            id={
              isAssociation
                ? 'customer-registration-kit.business-activity.associations.subtitle'
                : 'customer-registration-kit.business-activity.subtitle'
            }
            values={{
              link: chunks => (
                <FAQLink
                  articleSlug="why_declare_tax_residence_information"
                  className={styles.link}
                >
                  {chunks}
                </FAQLink>
              ),
            }}
          />
        </p>
      </div>
      <div className={styles.sectionWrapper}>
        {filteredActivities.map(activity => (
          <Fragment key={activity.key}>
            {activity.showDividerBefore ? <div className={styles.divider} /> : null}
            <Selector
              data-testid={`business-activity-${activity.key}`}
              isPending={loadingActivity === activity.value}
              onPress={() => {
                onSelect(activity.value);
              }}
              subtitle={formatMessage({
                // oxlint-disable-next-line @repo/no-dynamic-intl-id
                id: isAssociation
                  ? `customer-registration-kit.business-activity.associations.${activity.key}.description`
                  : `customer-registration-kit.business-activity.${activity.key}.description`,
              })}
              title={formatMessage({
                // oxlint-disable-next-line @repo/no-dynamic-intl-id
                id: `customer-registration-kit.business-activity.${activity.key}.title`,
              })}
            />
          </Fragment>
        ))}
      </div>
    </div>
  );
}
